import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Tag, Plus, Edit, RefreshCw, Loader2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import ticketTagService from '../../api/ticketTagService';
import { getRoleCodeFromId } from '../../utils/roleMapper';

const ALLOWED_ROLES = new Set(['ADMIN', 'SUPER_ADMIN']);

const emptyForm = () => ({
  tagName: '',
  color: '#3B82F6',
  description: ''
});

const TagManagement = () => {
  const roleId = Number(sessionStorage.getItem('roleId') || sessionStorage.getItem('role_id') || 0);
  const roleCode = (getRoleCodeFromId(roleId) || sessionStorage.getItem('roleCode') || '').toUpperCase();
  const canAccess = ALLOWED_ROLES.has(roleCode);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  const loadTags = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const data = await ticketTagService.listForAdmin(true);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      toast.error('Unable to load tags');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canAccess]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  if (!canAccess) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const filteredRows = rows.filter((row) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      row.tagName?.toLowerCase().includes(q)
      || row.description?.toLowerCase().includes(q)
    );
  });

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      tagName: row.tagName || '',
      color: row.color || '#3B82F6',
      description: row.description || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const tagName = formData.tagName.trim();
    if (!tagName) {
      toast.error('Tag name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tagName,
        color: formData.color?.trim() || null,
        description: formData.description?.trim() || null
      };
      if (editing?.tagId) {
        await ticketTagService.update(editing.tagId, payload);
        toast.success('Tag updated successfully');
      } else {
        await ticketTagService.create(payload);
        toast.success('Tag created successfully');
      }
      setShowModal(false);
      await loadTags();
    } catch (error) {
      console.error('Error saving tag:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Unable to save tag');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row) => {
    const next = !(row.active !== false);
    try {
      await ticketTagService.updateStatus(row.tagId, next);
      setRows((prev) => prev.map((r) => (r.tagId === row.tagId ? { ...r, active: next } : r)));
      toast.success(`Tag ${next ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating tag status:', error);
      toast.error('Unable to update tag status');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete tag "${row.tagName}"? This is only allowed when not assigned to tickets.`)) {
      return;
    }
    try {
      await ticketTagService.remove(row.tagId);
      toast.success('Tag deleted successfully');
      await loadTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error(error?.response?.data?.message || 'Unable to delete tag');
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Tag className="h-7 w-7 text-primary" />
            Ticket Tags
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Predefined tenant tags for classifying tickets (admin-managed).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadTags} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Tag
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left p-3 text-sm font-semibold">Name</th>
                  <th className="text-left p-3 text-sm font-semibold">Color</th>
                  <th className="text-left p-3 text-sm font-semibold">Description</th>
                  <th className="text-left p-3 text-sm font-semibold">Status</th>
                  <th className="text-left p-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      Loading...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      No tags found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.tagId} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3 text-sm font-medium">{row.tagName}</td>
                      <td className="p-3">
                        <span
                          className="inline-block h-4 w-4 rounded-full border border-border"
                          style={{ backgroundColor: row.color || '#e5e7eb' }}
                          title={row.color || 'default'}
                        />
                      </td>
                      <td className="p-3 text-sm text-muted-foreground max-w-xs truncate" title={row.description || ''}>
                        {row.description || '—'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={row.active !== false}
                            onCheckedChange={() => handleToggleStatus(row)}
                          />
                          <Badge className={row.active !== false
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-gray-100 text-gray-700 border-gray-300'}>
                            {row.active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(row)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Modal isOpen={showModal} onClose={() => !saving && setShowModal(false)} title={editing ? 'Edit Tag' : 'Add Tag'}>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Tag Name *</label>
            <Input
              value={formData.tagName}
              maxLength={50}
              onChange={(e) => setFormData((prev) => ({ ...prev, tagName: e.target.value }))}
              placeholder="e.g. urgent"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Color</label>
            <Input
              type="color"
              value={formData.color || '#3B82F6'}
              onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
              className="h-10 w-20 p-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Description</label>
            <Textarea
              value={formData.description}
              rows={3}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TagManagement;
